/**
 * AppService.ts
 * Service global pour accéder à AppCoordinator depuis n'importe où dans l'application
 * Utilisé notamment par les composants React
 */
import type { AppCoordinatorType } from '../types/AppCoordinatorTypes';
import { EventBus } from './EventBus';
import { logger } from '../utils/ProfessionalLogger';

// Type pour les listeners de changement
type CoordinatorChangeListener = (coordinator: AppCoordinatorType | null) => void;

// Type pour le snapshot retourné par getSnapshot
type CoordinatorSnapshot = { coordinator: AppCoordinatorType | null; version: number };

class AppService {
  private appCoordinator: AppCoordinatorType | null = null;
  private eventBus: EventBus;
  private listeners: Set<CoordinatorChangeListener> = new Set();
  // CORRIGÉ: Ajouter un compteur de version pour forcer la détection des changements
  private version: number = 0;
  // CORRIGÉ: Mémoriser le snapshot pour éviter de créer un nouvel objet à chaque fois
  private cachedSnapshot: CoordinatorSnapshot = { coordinator: null, version: 0 };

  constructor() {
    this.eventBus = EventBus.getInstance();
  }

  /**
   * S'abonne aux changements de AppCoordinator
   * CORRIGÉ: useSyncExternalStore appelle déjà getSnapshot() après la subscription
   * donc on n'a pas besoin de notifier immédiatement - cela peut causer des problèmes
   * On notifie seulement quand la valeur CHANGE après l'initialisation
   */
  subscribe(listener: CoordinatorChangeListener): () => void {
    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'AppService.ts:33',
          message: 'subscribe called',
          data: {
            currentListenersCount: this.listeners.size,
            coordinatorAvailable: this.appCoordinator !== null,
          },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'C',
        });
      })
      .catch(() => {});
    // #endregion

    this.listeners.add(listener);

    // CORRIGÉ: NE PAS notifier immédiatement car useSyncExternalStore appelle déjà getSnapshot()
    // après la subscription pour obtenir la valeur initiale
    // Notifier immédiatement peut causer des problèmes car onStoreChange() est appelé
    // avant que React n'ait eu le temps d'appeler getSnapshot() pour la valeur initiale
    // On notifie seulement quand la valeur CHANGE via notifyListeners()

    // Retourner la fonction de désabonnement
    return () => {
      this.listeners.delete(listener);
      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'AppService.ts:58',
            message: 'Listener unsubscribed',
            data: {
              remainingListenersCount: this.listeners.size - 1,
            },
            sessionId: 'debug-session',
            runId: 'run1',
            hypothesisId: 'C',
          });
        })
        .catch(() => {});
      // #endregion
    };
  }

  /**
   * Notifie tous les listeners d'un changement
   */
  private notifyListeners(): void {
    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'AppService.ts:82',
          message: 'notifyListeners called',
          data: {
            listenersCount: this.listeners.size,
            coordinatorAvailable: this.appCoordinator !== null,
            version: this.version,
          },
          sessionId: 'debug-session',
          runId: 'run2',
          hypothesisId: 'M',
        });
      })
      .catch(() => {});
    // #endregion

    let listenerIndex = 0;
    this.listeners.forEach(listener => {
      const currentIndex = listenerIndex++;
      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'AppService.ts:105',
            message: 'notifyListeners calling listener',
            data: {
              listenerIndex: currentIndex,
              coordinatorAvailable: this.appCoordinator !== null,
            },
            sessionId: 'debug-session',
            runId: 'run2',
            hypothesisId: 'M',
          });
        })
        .catch(() => {});
      // #endregion

      try {
        listener(this.appCoordinator);
        // #region agent log
        import('../utils/debugLogger')
          .then(({ debugLog }) => {
            debugLog({
              location: 'AppService.ts:125',
              message: 'notifyListeners listener called successfully',
              data: {
                listenerIndex: currentIndex,
              },
              sessionId: 'debug-session',
              runId: 'run2',
              hypothesisId: 'M',
            });
          })
          .catch(() => {});
        // #endregion
      } catch (error) {
        logger.error('Error in AppService listener', { error }, 'AppService');
        // #region agent log
        import('../utils/debugLogger')
          .then(({ debugLog }) => {
            debugLog({
              location: 'AppService.ts:140',
              message: 'Error in AppService listener',
              data: {
                listenerIndex: currentIndex,
                error: error instanceof Error ? error.message : String(error),
              },
              sessionId: 'debug-session',
              runId: 'run2',
              hypothesisId: 'M',
            });
          })
          .catch(() => {});
        // #endregion
      }
    });
  }

  /**
   * Définit l'instance AppCoordinator
   * CORRIGÉ: Émet un événement pour notifier les composants React
   * Émet l'événement même si AppCoordinator était déjà défini (pour les composants qui se montent après)
   */
  setAppCoordinator(coordinator: AppCoordinatorType, forceEmit: boolean = false): void {
    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'AppService.ts:143',
          message: 'setAppCoordinator called',
          data: {
            wasNull: this.appCoordinator === null,
            forceEmit,
            coordinatorProvided: coordinator !== null,
            listenersCount: this.listeners.size,
          },
          sessionId: 'debug-session',
          runId: 'run2',
          hypothesisId: 'M',
        });
      })
      .catch(() => {});
    // #endregion

    const wasNull = this.appCoordinator === null;
    const coordinatorChanged = this.appCoordinator !== coordinator;
    this.appCoordinator = coordinator;

    // CORRIGÉ: Incrémenter la version pour forcer la détection des changements
    // CRITIQUE: Toujours incrémenter la version et créer un NOUVEL objet snapshot
    // pour garantir que React détecte le changement (Object.is() comparaison)
    if (wasNull || coordinatorChanged || forceEmit) {
      this.version++;
      // CRITIQUE: Créer un NOUVEL objet pour forcer la détection par React
      // React compare avec Object.is(), donc une nouvelle référence = changement détecté
      this.cachedSnapshot = { coordinator: this.appCoordinator, version: this.version };

      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'AppService.ts:180',
            message: 'setAppCoordinator created new snapshot',
            data: {
              wasNull,
              coordinatorChanged,
              forceEmit,
              version: this.version,
              coordinatorAvailable: this.appCoordinator !== null,
              listenersCount: this.listeners.size,
            },
            sessionId: 'debug-session',
            runId: 'run2',
            hypothesisId: 'M',
          });
        })
        .catch(() => {});
      // #endregion
    }

    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'AppService.ts:58',
          message: 'setAppCoordinator called',
          data: {
            wasNull,
            forceEmit,
            coordinatorAvailable: coordinator !== null,
            coordinatorChanged,
            version: this.version,
          },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'C',
        });
      })
      .catch(() => {});
    // #endregion

    // Notifier tous les listeners (subscription directe)
    this.notifyListeners();

    // Notifier les composants React que AppCoordinator est maintenant disponible
    // Émettre si: 1) c'était null avant, OU 2) forceEmit est true (pour les composants qui se montent après)
    if ((wasNull || forceEmit) && coordinator !== null) {
      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'AppService.ts:210',
            message: 'Emitting app-coordinator-available event',
            data: {
              wasNull,
              forceEmit,
              listenersCount: this.listeners.size,
            },
            sessionId: 'debug-session',
            runId: 'run2',
            hypothesisId: 'M',
          });
        })
        .catch(() => {});
      // #endregion
      this.eventBus.emit({
        type: 'app-coordinator-available',
        data: { coordinator },
        timestamp: Date.now(),
      });
    }

    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'AppService.ts:228',
          message: 'setAppCoordinator calling notifyListeners',
          data: {
            listenersCount: this.listeners.size,
            coordinatorAvailable: this.appCoordinator !== null,
          },
          sessionId: 'debug-session',
          runId: 'run2',
          hypothesisId: 'M',
        });
      })
      .catch(() => {});
    // #endregion
  }

  /**
   * Obtient l'instance AppCoordinator
   */
  getAppCoordinator(): AppCoordinatorType | null {
    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'AppService.ts:21',
          message: 'getAppCoordinator entry',
          data: { hasCoordinator: this.appCoordinator !== null },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'C',
        });
      })
      .catch(() => {});
    // #endregion
    // Fallback: Si AppCoordinator n'est pas disponible, essayer de le récupérer depuis AppOrchestrator
    if (!this.appCoordinator) {
      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'AppService.ts:24',
            message: 'AppCoordinator null, trying fallback',
            data: {},
            sessionId: 'debug-session',
            runId: 'run1',
            hypothesisId: 'C',
          });
        })
        .catch(() => {});
      // #endregion
      interface WindowWithAppOrchestrator extends Window {
        __appOrchestrator?: {
          getAppCoordinator?: () => AppCoordinatorType | null;
        };
      }
      const appOrchestrator = (window as WindowWithAppOrchestrator).__appOrchestrator;
      if (appOrchestrator && typeof appOrchestrator.getAppCoordinator === 'function') {
        const coordinator = appOrchestrator.getAppCoordinator();
        // #region agent log
        import('../utils/debugLogger')
          .then(({ debugLog }) => {
            debugLog({
              location: 'AppService.ts:32',
              message: 'Fallback coordinator retrieved',
              data: { coordinatorAvailable: coordinator !== null },
              sessionId: 'debug-session',
              runId: 'run1',
              hypothesisId: 'C',
            });
          })
          .catch(() => {});
        // #endregion
        if (coordinator) {
          const wasNull = this.appCoordinator === null;
          const coordinatorChanged = this.appCoordinator !== coordinator;
          this.appCoordinator = coordinator;
          // CORRIGÉ: Incrémenter la version pour forcer la détection des changements
          // (wasNull sera toujours true ici car on est dans le bloc if (!this.appCoordinator))
          if (wasNull || coordinatorChanged) {
            this.version++;
            // CORRIGÉ: Mettre à jour le snapshot en cache
            this.cachedSnapshot = { coordinator: this.appCoordinator, version: this.version };
          }
          // Notifier les listeners que AppCoordinator est maintenant disponible
          this.notifyListeners();
        }
      }
    }
    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'AppService.ts:38',
          message: 'getAppCoordinator exit',
          data: { returningCoordinator: this.appCoordinator !== null },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'C',
        });
      })
      .catch(() => {});
    // #endregion
    return this.appCoordinator;
  }

  /**
   * Vérifie si AppCoordinator est disponible
   */
  isAvailable(): boolean {
    return this.appCoordinator !== null;
  }

  /**
   * Obtient la version actuelle (pour le debugging)
   */
  getVersion(): number {
    return this.version;
  }

  /**
   * Obtient le snapshot actuel (pour useSyncExternalStore)
   * CORRIGÉ: Retourne toujours un nouvel objet si la version a changé pour forcer la détection par React
   * BUG FIX: Créer un nouvel objet à chaque fois que la version change pour garantir que React détecte le changement
   */
  getSnapshot(): CoordinatorSnapshot {
    // CORRIGÉ: Toujours s'assurer que le cache est à jour
    // (même si setAppCoordinator devrait déjà l'avoir mis à jour, on vérifie pour éviter les race conditions)
    // IMPORTANT: Créer un NOUVEL objet si la version ou le coordinator a changé
    // pour garantir que React détecte le changement (Object.is() comparaison)
    const needsUpdate =
      this.cachedSnapshot.version !== this.version ||
      this.cachedSnapshot.coordinator !== this.appCoordinator;

    if (needsUpdate) {
      // #region agent log
      import('../utils/debugLogger')
        .then(({ debugLog }) => {
          debugLog({
            location: 'AppService.ts:322',
            message: 'getSnapshot creating new snapshot object',
            data: {
              oldVersion: this.cachedSnapshot.version,
              newVersion: this.version,
              coordinatorChanged: this.cachedSnapshot.coordinator !== this.appCoordinator,
              oldCoordinatorNull: this.cachedSnapshot.coordinator === null,
              newCoordinatorNull: this.appCoordinator === null,
            },
            sessionId: 'debug-session',
            runId: 'run1',
            hypothesisId: 'C',
          });
        })
        .catch(() => {});
      // #endregion
      // CRITIQUE: Créer un NOUVEL objet pour forcer la détection par React
      // React compare avec Object.is(), donc une nouvelle référence = changement détecté
      this.cachedSnapshot = { coordinator: this.appCoordinator, version: this.version };
    }

    // #region agent log
    import('../utils/debugLogger')
      .then(({ debugLog }) => {
        debugLog({
          location: 'AppService.ts:346',
          message: 'getSnapshot returning',
          data: {
            version: this.cachedSnapshot.version,
            coordinatorAvailable: this.cachedSnapshot.coordinator !== null,
            snapshotReference: needsUpdate ? 'new' : 'cached',
          },
          sessionId: 'debug-session',
          runId: 'run1',
          hypothesisId: 'C',
        });
      })
      .catch(() => {});
    // #endregion

    return this.cachedSnapshot;
  }
}

// Export singleton
export const appService = new AppService();

// Ajouter un identifiant unique pour vérifier que c'est le même singleton
interface WindowWithAppService extends Window {
  __appServiceInstance?: typeof appService;
}
(window as WindowWithAppService).__appServiceInstance = appService;
